/*
 * The admin site's words in Danish: the releaseNotes section - line for line
 * releaseNotes.en.ts, which is the contract.
 */

import type { releaseNotesEn } from './releaseNotes.en.js';

export const releaseNotesDa: typeof releaseNotesEn = {
  /** Words more than one of the release-note pages uses. */
  common: {
    releaseNotes: 'Udgivelsesnoter',
    newReleaseNote: 'Ny udgivelsesnote',
    untitled: '(uden titel)',
    title: 'Titel',
    properties: 'Egenskaber',
    titleRequired: 'Angiv en titel.',
    titleSingleLine: 'En udgivelsesnotes titel skal stå på én linje.',
  },

  /** ReleaseNoteListPage. */
  list: {
    matching: (count) => `${count} ${count === 1 ? 'resultat' : 'resultater'}`,
    deleted: (label, performed) => `»${label}« er slettet. ${performed}`,
    search: 'Søg',
    searchPlaceholder: 'Titel eller version…',
    state: 'Status',
    product: 'Produkt',
    anyProduct: 'Alle',
    sort: 'Sortering',
    sorts: {
      modified: 'Senest redigeret',
      created: 'Nyeste',
      publish: 'Udgivelsesdato',
      title: 'Titel',
    },
    statuses: {
      all: 'Alle udgivelsesnoter',
      live: 'Live',
      scheduled: 'Planlagt',
      hidden: 'Skjult',
      draft: 'Ikke-udgivet kladde',
      withdrawn: 'Tekst trukket tilbage',
      unwritten: 'Endnu ikke skrevet',
    },
    loading: 'Indlæser udgivelsesnoter…',
    noneMatch: 'Ingen udgivelsesnoter matcher',
    noneMatchHint: 'Ret filtrene, eller skriv den første udgivelsesnote.',
    columnReleaseNote: 'Udgivelsesnote',
    columnState: 'Status',
    columnPublish: 'Udgivelse',
    columnEdited: 'Redigeret',
    columnActions: 'Handlinger',
    draft: 'kladde',
    draftNewer: (latest, current) =>
      `Revision ${latest} er nyere end den udgivne revision ${current ?? '—'}.`,
    edit: 'Rediger',
    delete: 'Slet',
  },

  /** NewReleaseNotePage. */
  create: {
    loadingMetadata: 'Indlæser metadata…',
    setUpMetadata: 'Opret metadata først',
    needsProduct:
      'En udgivelsesnote skal have et produkt - det er halvdelen af dens adresse - og der findes endnu ingen produkter.',
    goToMetadata: 'Gå til Metadata',
    breadcrumbNew: 'Ny',
    intro:
      'Udgivelsesnoten oprettes med sin første revision, så den har noget at vise, så snart den udgives.',
    content: 'Tekst',
    productRequired: 'Vælg et produkt.',
    publishing: 'Udgivelse',
    whenVisible: 'Hvornår skal læserne kunne se den',
    publishModes: {
      hidden: 'Hold den skjult indtil videre',
      now: 'Udgiv med det samme',
      schedule: 'Planlæg til senere',
    },
    publishModeHints: {
      hidden: 'Læserne kan ikke se noget, før du udgiver den.',
      now: 'Kan læses, så snart den er oprettet.',
      schedule: 'Kan læses fra det valgte tidspunkt.',
    },
    publishAt: 'Udgivelsestidspunkt (din lokale tid)',
    storedAs: (iso) => `Gemmes som ${iso}`,
    creating: 'Opretter…',
    createReleaseNote: 'Opret udgivelsesnote',
    cancel: 'Annuller',
  },

  /** ReleaseNoteEditorPage. */
  editor: {
    loading: 'Indlæser udgivelsesnoten…',
    deleted: 'Slettet',
    deletedEverything: (label) => `»${label}« og alt, hvad der hører til den, er slettet.`,
    backToList: 'Tilbage til udgivelsesnoter',
    byline: '{product} {version} · oprettet {created} · seneste aktivitet {modified}',
    viewOnSite: 'Vis på webstedet',
    deleteReleaseNote: 'Slet udgivelsesnote',
    savedAndPublished: (revision) => `Revision ${revision} er gemt og udgivet.`,
    savedAsDraft: (revision, readersSee) =>
      `Revision ${revision} er gemt som kladde. Læserne ser stadig revision ${readersSee ?? '—'}.`,
    saveBoxHeading: 'Udgivelsesnote',
    changeNotePlaceholder: 'f.eks. Tilføjede rettelsen af timeout ved eksport',
    outOfDate: 'Din kopi er forældet.',
    outOfDateHelp:
      'Kopiér din tekst til et sikkert sted, genindlæs siden, og foretag din ændring igen oven på den andens. Intet er blevet overskrevet.',
    text: 'Tekst',
    draftBadge: (revision) => `kladde #${revision}`,
    nothingWritten: 'intet skrevet endnu',
    editingDraft: (latest, current) =>
      `Du redigerer kladderevision ${latest}; læserne ser revision ${current ?? '—'}.`,
    publishToShow: 'Gem og udgiv for at gøre dette til den version, læserne ser.',
  },

  /** ReleaseNoteProperties. */
  properties: {
    addressMoved: (next, previous) =>
      `Adressen er nu ${next}. Links til ${previous}, som er skrevet i hånden andre steder, fører ikke længere hertil.`,
    address: 'Adresse: {address}',
    addressMoving: 'Adresse: {address} — ændrer du produktet eller versionen, flyttes udgivelsesnoten til denne adresse.',
    saving: 'Gemmer…',
    save: 'Gem egenskaber',
    unsaved: 'Ikke-gemte ændringer',
  },

  /** ReleaseNoteVersionInput. */
  versionInput: {
    label: 'Version',
    hint: 'Bogstavet v er en del af versionen og står der allerede: skriv de fire tal efter det, for eksempel 1.2.3.4.',
  },
};
